import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NotifierService } from 'angular-notifier';
import { CancelModal } from 'src/app/shared/modals/cancel/cancel-modal.component';
import { RegisterService } from '../../../../shared/services/register.service';
import { AuthenticationService } from '../../../../shared/services/authentication.service';
import { UserService } from '../../../../shared/services/user.service';
import { AddressService } from '../../../../shared/services/address.service';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { BoobleModalService } from 'booble-modal';
import { UseTermsModal } from 'src/app/shared/modals/use-terms/use-terms-modal.component';
import { PrivacyPolicyModal } from 'src/app/shared/modals/privacy-policy/privacy-policy-modal.component';
import { ContactDto } from 'src/app/domain/models/dto/contact.dto';
import { AddressDto } from 'src/app/domain/models/dto/address.dto';
import { CorporateAccountDto } from 'src/app/domain/models/dto/corporate-account.dto';
import { CorporateAccountService } from 'src/app/shared/services/corporate-account.service';
import { RegisterStepType } from 'src/app/domain/enums/register-step-types.enum';
import { BankAccountService } from 'src/app/shared/services/bank-account.service';
import { CepResponse } from 'src/app/domain/models/interfaces/cep-response.interface';
import { cnpjValidator } from 'src/app/shared/validators/form-validators';

@Component({
  selector: 'app-data',
  templateUrl: './data.component.html',
  styleUrls: ['./data.component.scss'],
})
export class DataComponent implements OnInit {
  private readonly notifier: NotifierService;
  userLogin: string;
  companyId: number;
  tenant: string = '';
  public loading: boolean;
  companyForm: FormGroup;

  get companyFormControl() {
    return this.companyForm.controls;
  }

  get company(): CorporateAccountDto {
    const company = this.companyForm.getRawValue();
    company.companyName = this.companyForm.controls['companyName'].value;
    company.cnpj = this.companyForm.controls['cnpj'].value;
    company.businessSector = this.companyForm.controls['businessSector'].value;
    company.id = this.companyId;
    company.contact = new ContactDto(
      company.phone,
      company.email,
      new AddressDto(
        company.line1,
        company.line2,
        company.line3,
        false,
        '',
        company.number,
        company.postalCode,
        'Brazil',
        company.city,
        company.state
      )
    );
    company.contact.id = company.contactId;
    company.contact.address.id = company.addressId;
    return company;
  }

  set company(company: CorporateAccountDto) {
    if (company) {
      company.id = Number(this.companyId);
      this.companyForm.patchValue(company);
      this.companyForm.controls['businessSector'].setValue(
        company.businessSector
      );
      if (company.contact) {
        this.companyForm.controls['contactId'].setValue(company.contact?.id);
        this.companyForm.controls['email'].setValue(company.contact?.email);
        this.companyForm.controls['phone'].setValue(company.contact?.phone);
        this.companyForm.controls['addressId'].setValue(
          company.contact?.address?.id
        );
        this.companyForm.controls['line1'].setValue(
          company.contact?.address?.line1
        );
        this.companyForm.controls['line2'].setValue(
          company.contact?.address?.line2
        );
        this.companyForm.controls['line3'].setValue(
          company.contact?.address?.line3
        );
        this.companyForm.controls['number'].setValue(
          company.contact?.address?.number
        );
        this.companyForm.controls['postalCode'].setValue(
          company.contact?.address?.postalCode
        );
        this.companyForm.controls['country'].setValue(
          company.contact?.address?.country
        );
        this.companyForm.controls['city'].setValue(
          company.contact?.address?.city ?? ''
        );
        this.companyForm.controls['state'].setValue(
          company.contact?.address?.state ?? ''
        );
      }
    }
  }

  constructor(
    fb: FormBuilder,
    notifierService: NotifierService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly registerService: RegisterService,
    private readonly authService: AuthenticationService,
    private readonly addressService: AddressService,
    private readonly companyService: CorporateAccountService,
    private readonly modalService: BoobleModalService
  ) {
    this.notifier = notifierService;
    this.userLogin = this.authService.getCurrentUserId();
    this.companyId = +this.authService.getCompanyId();

    this.companyForm = fb.group({
      id: [],
      cnpj: ['', [Validators.required, Validators.pattern('.*\\S.*'), cnpjValidator]],
      companyName: ['', [Validators.required, Validators.pattern('.*\\S.*')]],
      fantasyName: [],
      phone: ['', [Validators.required, Validators.pattern('.*\\S.*')]],
      email: ['', [Validators.required, Validators.email]],
      businessSector: [
        '',
        [Validators.required, Validators.pattern('.*\\S.*')],
      ],
      annualBilling: ['', [Validators.required, Validators.min(1)]],
      contactId: [],
      addressId: [],
      line1: ['', [Validators.required, Validators.pattern('.*\\S.*')]],
      city: ['', [Validators.required, Validators.pattern('.*\\S.*')]],
      state: ['', [Validators.required, Validators.pattern('.*\\S.*')]],
      line2: [],
      line3: [],
      postalCode: ['', [Validators.required, Validators.pattern('.*\\S.*')]],
      number: ['', [Validators.required, Validators.pattern('.*\\S.*')]],
      country: ['Brazil'],
    });
  }

  ngOnInit(): void {
    this.companyService.get().subscribe((getResult) => {
      this.company = getResult.data;
      this.companyForm.controls['cnpj'].disable();
      this.companyForm.controls['companyName'].disable();
      this.companyForm.controls['businessSector'].disable();
      this.companyForm.controls['postalCode'].disable();
      this.companyForm.controls['line1'].disable();
      this.companyForm.controls['line3'].disable();
      this.companyForm.controls['city'].disable();
      this.companyForm.controls['state'].disable();
    });

    this.route.paramMap.subscribe((params) => {
      const result = params.get('tenant');

      if (result) this.tenant = params.get('tenant');
    });
  }

  getCompanyCep(cep: string) {
    this.addressService.getCep(cep).subscribe((res: CepResponse) => {
      if (res.error) {
        this.companyForm.controls['line1'].enable();
        this.companyForm.controls['line3'].enable();
        this.companyForm.controls['city'].enable();
        this.companyForm.controls['state'].enable();
        this.notifier.notify(
          'warning',
          'Não foi possível buscar o CEP. Preencha o endereço manualmente.'
        );
      } else {
        if (res.street) {
          this.companyForm.controls['line1'].setValue(res.street);
        }
        if (res.neighborhood) {
          this.companyForm.controls['line3'].setValue(res.neighborhood);
        }
        if (res.city) {
          this.companyForm.controls['city'].setValue(res.city);
        }
        if (res.state) {
          this.companyForm.controls['state'].setValue(res.state);
        }
      }
    });
  }

  changePhoneCompanyForm(text: String) {
    this.companyForm.controls['phone'].patchValue(text);
  }

  register() {
    if (this.companyId || +this.companyId !== 0) {
      this.update();
    }
  }

  update() {
    if (this.companyForm.valid) {
      this.loading = true;

      this.companyService
        .putRegister(this.company, Number(this.companyId))
        .subscribe({
          next: () => {
            this.registerService
              .putRegisterStep(RegisterStepType.RegisterData)
              .subscribe({
                next: (stepResult) => {
                  this.router.navigate([
                    `${this.tenant}/onboardings/responsible`,
                  ]);
                },
                error: (err) => {
                  this.loading = false;
                },
              });
          },
          error: (err) => {
            this.loading = false;
          },
        });
    }
  }

  openTermsModal() {
    this.modalService.present(UseTermsModal);
  }

  openPrivacyPolicyModal() {
    this.modalService.present(PrivacyPolicyModal);
  }
}
